import { readDevPasswordHash } from './devPassword';

interface AppConfig {
  GRAPHQL_URL: string;
  DOCUMENT_URL: string;
  EXPORT_URL?: string;
  DEV_TOOLS?: boolean;
  DEV_PASSWORD?: boolean;
}

let config: AppConfig | null = null;

export async function loadConfig(): Promise<AppConfig> {
  if (config) {
    return config;
  }

  try {
    const response = await fetch('/config.json');
    if (!response.ok) {
      throw new Error(`Failed to load config.json: ${response.statusText}`);
    }
    const loadedConfig: AppConfig = await response.json();
    config = loadedConfig;
    return loadedConfig;
  } catch (error) {
    console.error('Error loading config.json:', error);
    throw error;
  }
}

export function getConfig(): AppConfig {
  if (!config) {
    throw new Error('Config not loaded. Call loadConfig() first.');
  }
  return config;
}

export function setConfig(newConfig: AppConfig): void {
  config = newConfig;
}

/**
 * Returns headers with the dev password hash if dev password mode is enabled.
 * Use this for all direct fetch() calls to the backend.
 */
export function getDevPasswordHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  if (config?.DEV_PASSWORD) {
    const stored = readDevPasswordHash();
    if (stored) {
      headers['X-Dev-Password-Hash'] = stored;
    }
  }
  return headers;
}

/**
 * Base URL of the department API, derived from the configured document endpoint. Used by the
 * fetch-based endpoints (documents, archive, message send) that sit next to /document.
 */
export function getApiBaseUrl(): string {
  return getConfig().DOCUMENT_URL.replace(/\/document$/, '');
}

/**
 * Builds a document URL, appending the dev password hash as a query param
 * when dev password mode is enabled (for direct browser navigation).
 *
 * Pass `forceDownload: true` to add `?download=true`, which makes the backend
 * respond with `Content-Disposition: attachment` regardless of file type. The
 * `<a download>` attribute alone is insufficient when the document endpoint is
 * cross-origin from the SPA — Chrome ignores it and opens PDFs inline.
 */
export function getDocumentUrl(
  documentId: string,
  options?: { forceDownload?: boolean },
): string {
  const endpoint = getConfig().DOCUMENT_URL;
  const params = new URLSearchParams();
  if (options?.forceDownload) {
    params.set('download', 'true');
  }
  if (config?.DEV_PASSWORD) {
    const hash = readDevPasswordHash();
    if (hash) params.set('hash', hash);
  }
  const query = params.toString();
  return query
    ? `${endpoint}/${documentId}?${query}`
    : `${endpoint}/${documentId}`;
}

export type { AppConfig };
